import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function getPdfLabels(lang = "de") {
  const labels = {
    sq: {
      invoiceTitle: "FATURË",
      number: "Nr.",
      date: "Data",
      due: "Afati i pagesës",
      billTo: "FATURA PËR",
      companyData: "TË DHËNAT E FIRMËS",
      recipientSection: "MARRËSI",
      description: "Përshkrimi",
      qty: "Sasia",
      price: "Çmimi / njësi",
      total: "Totali",
      subtotal: "Nëntotali",
      vat: "TVSH",
      grandTotal: "Gjithsej",
      notes: "SHËNIME",
      bank: "Banka",
      iban: "IBAN",
      taxNo: "Nr. Tatimor",
      businessNo: "Nr. Biznesit",
      thankYou: "Faleminderit për besimin tuaj!",
      page: "Faqja",
    },
    en: {
      invoiceTitle: "INVOICE",
      number: "No.",
      date: "Date",
      due: "Due Date",
      billTo: "BILL TO",
      companyData: "COMPANY DETAILS",
      recipientSection: "RECIPIENT",
      description: "Description",
      qty: "Quantity",
      price: "Price/Unit",
      total: "Total",
      subtotal: "Subtotal",
      vat: "VAT",
      grandTotal: "Grand Total",
      notes: "NOTES",
      bank: "Bank",
      iban: "IBAN",
      taxNo: "Tax No.",
      businessNo: "Business No.",
      thankYou: "Thank you for your trust!",
      page: "Page",
    },
    de: {
      invoiceTitle: "RECHNUNG",
      number: "Nr.",
      date: "Datum",
      due: "Fälligkeitsdatum",
      billTo: "RECHNUNG AN",
      companyData: "FIRMENDATEN",
      recipientSection: "EMPFÄNGERNAME",
      description: "Beschreibung",
      qty: "Menge",
      price: "Preis/Einheit",
      total: "Gesamt",
      subtotal: "Zwischensumme",
      vat: "MwSt.",
      grandTotal: "Gesamtsumme",
      notes: "NOTIZEN",
      bank: "Bank",
      iban: "IBAN",
      taxNo: "Steuer-Nr.",
      businessNo: "UID",
      thankYou: "Vielen Dank für Ihr Vertrauen!",
      page: "Seite",
    },
  };

  return labels[lang] || labels.de;
}

function formatMoney(value, currency = "EUR", lang = "de") {
  const localeMap = {
    sq: "sq-AL",
    de: "de-DE",
    en: "en-GB",
  };

  return new Intl.NumberFormat(localeMap[lang], {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function drawRoundedBox(doc, x, y, w, h, fill = [255, 255, 255], stroke = [219, 229, 242], radius = 5) {
  doc.setFillColor(...fill);
  doc.setDrawColor(...stroke);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, radius, radius, "FD");
}

function drawLabel(doc, text, x, y, color = [148, 163, 184]) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...color);
  doc.text(String(text || "").toUpperCase(), x, y);
}

function drawValue(doc, text, x, y, opts = {}) {
  doc.setFont("helvetica", opts.bold ? "bold" : "normal");
  doc.setFontSize(opts.size || 9);
  doc.setTextColor(...(opts.color || [15, 23, 42]));
  
  const options = {};
  if (opts.align) {
    options.align = opts.align;
  }
  
  doc.text(String(text || ""), x, y, options);
}

function safeText(value) {
  return String(value || "").trim();
}

function isMeaningfulValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

function mergeInvoiceData(...sources) {
  return sources.reduce((acc, source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (isMeaningfulValue(value)) acc[key] = value;
    });
    return acc;
  }, {});
}

function buildClientLines(client) {
  const addressLines = safeText(client.address)
    .split(/\r?\n|,/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 4);

  if (addressLines.length) return addressLines;

  return [
    safeText(client.email),
    safeText(client.phone),
  ].filter(Boolean);
}

function buildSenderLines(company) {
  const cityLine = [safeText(company.postalCode), safeText(company.city)].filter(Boolean).join(' ');
  const primary = [
    safeText(company.ownerName || company.companyName),
    safeText(company.address),
    cityLine,
    safeText(company.country),
  ].filter(Boolean);

  if (primary.length > 1) return primary;

  return [
    safeText(company.ownerName || company.companyName),
    safeText(company.email),
    safeText(company.phone),
    safeText(company.website),
  ].filter(Boolean);
}

function buildSenderInline(company) {
  const cityLine = [safeText(company.postalCode), safeText(company.city)].filter(Boolean).join(' ');
  const parts = [
    safeText(company.ownerName || company.companyName),
    safeText(company.address),
    cityLine,
    safeText(company.country),
  ].filter(Boolean);

  if (parts.length >= 2) return parts.join(' - ').toUpperCase();

  return buildSenderLines(company)
    .slice(0, 4)
    .join(' - ')
    .toUpperCase();
}

function fitFontSizeToWidth(doc, text, maxWidth, startSize = 8.2, minSize = 5.8) {
  const content = String(text || '');
  let size = startSize;
  doc.setFontSize(size);

  while (size > minSize && doc.getTextWidth(content) > maxWidth) {
    size -= 0.2;
    doc.setFontSize(size);
  }

  return size;
}

function buildBrandLines(company) {
  const preferred = [
    safeText(company.email),
    safeText(company.phone),
    safeText(company.website),
  ].filter(Boolean);

  if (preferred.length) return preferred.slice(0, 3);

  const fallbackCityLine = [safeText(company.postalCode), safeText(company.city)].filter(Boolean).join(' ');
  return [
    safeText(company.address),
    fallbackCityLine,
    safeText(company.country),
  ].filter(Boolean).slice(0, 3);
}

function loadImageElement(src, useCors = false) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCors) {
      img.crossOrigin = "anonymous";
      img.referrerPolicy = "no-referrer";
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function imageElementToDataUrl(img) {
  const canvas = document.createElement("canvas");
  const width = Math.max(img.naturalWidth || img.width || 0, 1);
  const height = Math.max(img.naturalHeight || img.height || 0, 1);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/png");
}

async function getPdfLogoDataUrl(src) {
  if (!src || typeof window === "undefined") return null;
  if (String(src).startsWith("data:image/")) return src;

  try {
    const response = await fetch(src, {
      mode: "cors",
      cache: "no-store",
    });
    if (!response.ok) throw new Error(`Logo fetch failed: ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    try {
      const img = await loadImageElement(objectUrl);
      return imageElementToDataUrl(img);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    try {
      const img = await loadImageElement(src, true);
      return imageElementToDataUrl(img);
    } catch (imgError) {
      console.warn("Unable to load logo for PDF", error, imgError);
      return null;
    }
  }
}

function drawContainedImage(doc, dataUrl, x, y, w, h, padding = 2) {
  if (!dataUrl) return false;

  try {
    const props = doc.getImageProperties(dataUrl);
    const innerW = Math.max(w - padding * 2, 1);
    const innerH = Math.max(h - padding * 2, 1);
    const ratio = Math.min(innerW / props.width, innerH / props.height);
    const drawW = props.width * ratio;
    const drawH = props.height * ratio;
    const drawX = x + (w - drawW) / 2;
    const drawY = y + (h - drawH) / 2;
    doc.addImage(dataUrl, "PNG", drawX, drawY, drawW, drawH, undefined, "FAST");
    return true;
  } catch (error) {
    console.warn("Unable to draw logo in PDF", error);
    return false;
  }
}

export default async function generateInvoicePDF(invoice = {}, company = {}, options = {}) {
  const lang = options.lang || invoice.lang || "de";
  const L = getPdfLabels(lang);
  const mergedCompany = mergeInvoiceData(invoice.company || {}, company || {});

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const currency = invoice.currency || mergedCompany.currency || "EUR";
  const pageW = 210;
  const pageH = 297;
  const left = 14;
  const right = 196;

  const invoiceNumber = invoice.invoiceNumber || invoice.number || "INVOICE";
  const invoiceDate = invoice.date || invoice.invoiceDate || "";
  const dueDate = invoice.dueDate || "";
  const companyName = mergedCompany.companyName || "BauHeld";

  const client = {
    name: invoice.client?.name || invoice.clientName || invoice.companyName || "—",
    email: invoice.client?.email || "",
    phone: invoice.client?.phone || "",
    address: invoice.client?.address || "",
  };

  const items = (invoice.items || []).map((item) => {
    const qty = Number(item.qty ?? item.quantity ?? 0);
    const price = Number(item.price ?? item.unitPrice ?? 0);
    const total = Number(item.total ?? qty * price);

    return {
      description: item.description || "—",
      qty,
      price,
      total,
    };
  });

  const subtotal = Number(invoice.subtotal ?? invoice.total ?? invoice.grandTotal ?? 0) || 0;
  const vatRate = Number(invoice.vatRate ?? 0) || 0;
  const vatAmount = invoice.vatAmount != null
    ? Number(invoice.vatAmount || 0)
    : Number(((subtotal * vatRate) / 100).toFixed(2));
  const grandTotal = Number(invoice.grandTotal ?? invoice.total ?? subtotal + vatAmount) || 0;

  // Background
  doc.setFillColor(245, 246, 248);
  doc.rect(0, 0, pageW, pageH, "F");

  let y = 24;

  // ========== LETTERHEAD ==========
  const senderInline = buildSenderInline(mergedCompany);
  const clientLines = buildClientLines(client);
  const brandLines = buildBrandLines(mergedCompany);
  const titleText = String(L.invoiceTitle || 'RECHNUNG').toUpperCase();
  const logoDataUrl = await getPdfLogoDataUrl(mergedCompany.logoUrl);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(30, 64, 145);
  doc.text(titleText, left + 2, y);

  const rightColW = 80;
  const logoBoxW = 24;
  const logoBoxH = 18;
  const brandBlockX = right - rightColW;
  const brandBlockY = y - 8;
  const brandTextX = brandBlockX + logoBoxW + 5;

  const logoDrawn = drawContainedImage(doc, logoDataUrl, brandBlockX, brandBlockY, logoBoxW, logoBoxH, 0);
  if (!logoDrawn) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(String(companyName || 'B').slice(0, 1).toUpperCase(), brandBlockX + logoBoxW / 2, brandBlockY + 11, { align: 'center' });
  }

  doc.setFont('times', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(21, 21, 21);
  doc.text(companyName, brandTextX, brandBlockY + 9);

  let brandInfoY = brandBlockY + 14.5;
  brandLines.forEach((line) => {
    drawValue(doc, line, brandTextX, brandInfoY, { size: 7.4, color: [31, 78, 163] });
    brandInfoY += 4;
  });

  const senderStartY = y + 28;
  let senderCursorY = senderStartY;
  const senderLineText = senderInline || String(companyName || '').toUpperCase();
  const senderMaxWidth = 92;

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(148, 163, 184);
  const senderFontSize = fitFontSizeToWidth(doc, senderLineText, senderMaxWidth, 7.8, 5.8);
  doc.setFontSize(senderFontSize);
  doc.text(senderLineText, left + 2, senderCursorY);
  senderCursorY += 14;

  drawValue(doc, L.recipientSection || L.billTo, left + 2, senderCursorY, { size: 10, color: [15, 23, 42], bold: true });
  const recipientName = safeText(client.name) || '—';

  let recipientY = senderCursorY + 7.5;
  drawValue(doc, recipientName, left + 2, recipientY, {
    size: 9.8,
    color: [51, 65, 85],
    bold: false,
  });
  recipientY += 5.6;

  (clientLines.length ? clientLines : ['—']).forEach((line) => {
    drawValue(doc, line, left + 2, recipientY, {
      size: 9.2,
      color: [51, 65, 85],
      bold: false,
    });
    recipientY += 5.6;
  });

  recipientY += 0.5;

  const metaBoxW = 72;
  const metaBoxH = 40;
  const metaBoxX = right - metaBoxW;
  const metaBoxY = senderStartY - 4;
  drawRoundedBox(doc, metaBoxX, metaBoxY, metaBoxW, metaBoxH, [242, 244, 247], [226, 232, 240], 4);

  const metaRows = [
    [lang === 'de' ? 'Rechnungsnr.:' : `${L.number}:`, invoiceNumber],
    [lang === 'de' ? 'Rechnungsdatum:' : `${L.date}:`, invoiceDate],
    [lang === 'de' ? 'Fälligkeitsdatum:' : `${L.due}:`, dueDate],
  ];

  let metaY = metaBoxY + 8.5;
  metaRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.8);
    doc.setTextColor(15, 23, 42);
    doc.text(String(label), metaBoxX + 5, metaY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(71, 85, 105);
    doc.text(String(value || '—'), metaBoxX + metaBoxW - 5, metaY, { align: 'right' });
    metaY += 9;
  });

  y = Math.max(recipientY + 8, metaBoxY + metaBoxH) + 10;

  // ========== ITEMS TABLE ==========
  autoTable(doc, {
    startY: y,
    margin: { left, right: 14 },
    head: [[L.description, L.qty, L.price, L.total]],
    body: items.map((item) => [
      item.description,
      item.qty.toString(),
      formatMoney(item.price, currency, lang),
      formatMoney(item.total, currency, lang),
    ]),
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: 4,
      textColor: [15, 23, 42],
      lineColor: [200, 215, 235],
      lineWidth: 0.1,
      valign: "middle",
    },
    headStyles: {
      fillColor: [6, 17, 38],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
      halign: "center",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: {
      0: { cellWidth: 88, halign: "left" },
      1: { cellWidth: 20, halign: "center" },
      2: { cellWidth: 36, halign: "right" },
      3: { cellWidth: 36, halign: "right", fontStyle: "bold" },
    },
    tableLineColor: [200, 215, 235],
    tableLineWidth: 0.2,
  });

  y = (doc.lastAutoTable?.finalY || y) + 12;

  // ========== FOOTER ==========
  const footerCol1X = left;
  const footerCol2X = 102;
  const footerColW = 82;

  // Notes section
  drawRoundedBox(doc, footerCol1X, y, footerColW, 28, [250, 251, 253], [220, 226, 234]);
  drawLabel(doc, L.notes, footerCol1X + 6, y + 7);
  
  // Accent line
  doc.setDrawColor(6, 17, 38);
  doc.line(footerCol1X + 6, y + 9, footerCol1X + 40, y + 9);

  drawValue(doc, invoice.notes || L.thankYou, footerCol1X + 6, y + 18, {
    size: 8,
    color: [50, 60, 80],
  });

  // Totals box
  const totalBoxX = footerCol2X;
  const totalBoxY = y;
  const totalBoxW = 94;
  const totalBoxH = 32;

  drawRoundedBox(doc, totalBoxX, totalBoxY, totalBoxW, totalBoxH, [250, 251, 253], [220, 226, 234], 5);

  const totalsPadding = 8;
  const lineHeightTotal = 7;

  // Subtotal
  drawValue(doc, L.subtotal, totalBoxX + totalsPadding, totalBoxY + totalsPadding, {
    size: 8,
    color: [100, 120, 140],
  });
  drawValue(doc, formatMoney(subtotal, currency, lang), totalBoxX + totalBoxW - totalsPadding, totalBoxY + totalsPadding, {
    size: 8,
    align: "right",
    bold: true,
  });

  // VAT (if exists)
  let vatPosition = totalBoxY + totalsPadding + lineHeightTotal;
  if (vatRate > 0 || vatAmount > 0) {
    drawValue(doc, `${L.vat} ${vatRate > 0 ? `(${vatRate}%)` : ''}`, totalBoxX + totalsPadding, vatPosition, {
      size: 8,
      color: [100, 120, 140],
    });
    drawValue(doc, formatMoney(vatAmount, currency, lang), totalBoxX + totalBoxW - totalsPadding, vatPosition, {
      size: 8,
      align: "right",
    });
    vatPosition += lineHeightTotal;
  }

  // Separator line
  doc.setDrawColor(200, 215, 235);
  doc.setLineWidth(0.2);
  doc.line(
    totalBoxX + totalsPadding,
    vatPosition - 2,
    totalBoxX + totalBoxW - totalsPadding,
    vatPosition - 2
  );

  // Grand Total
  drawValue(doc, L.grandTotal, totalBoxX + totalsPadding, vatPosition + 2, {
    bold: true,
    size: 9,
    color: [6, 17, 38],
  });
  drawValue(doc, formatMoney(grandTotal, currency, lang), totalBoxX + totalBoxW - totalsPadding, vatPosition + 2, {
    bold: true,
    size: 9,
    align: "right",
    color: [6, 17, 38],
  });

  // Page footer
  const footerY = pageH - 10;
  doc.setDrawColor(200, 215, 235);
  doc.setLineWidth(0.2);
  doc.line(left, footerY - 3, right, footerY - 3);
  
  drawValue(doc, `${L.page} 1`, right, footerY, {
    size: 7,
    color: [100, 120, 140],
    align: "right",
  });

  // ========== SAVE PDF ==========
  const safeClientName = safeText(client.name || "client")
    .replace(/\s+/g, "_")
    .replace(/[^\w-]/g, "");
  
  const cleanDate = invoiceDate.replace(/[^0-9]/g, '');
  const fileName = `${invoiceNumber}_${safeClientName}_${cleanDate || 'nodate'}.pdf`;
  doc.save(fileName);
}